import { Hash } from '~/interfaces/hashInterfaces'
import { IEmbeddedModelHash } from "~/interfaces/modelInterfaces"
import {IProcessFlowVariable, IRedirectStep, ISendNotificationStep, TProcessFlowStepSpecific} from '~/interfaces/processFlowInterfaces'
import { IReferencePathNodeSpecific } from '~/interfaces/referencePathInterfaces'
import { IVOptionSetLocal } from '~/interfaces/vSelectInterfaces'
import FindCountStepConditionalRuleSet from "~/models/processFlows/findCountStepConditionalRuleSet"
import FindDataStepConditionalRuleSet from "~/models/processFlows/findDataStepConditionalRuleSet"
import { IOVariableLink } from "~/models/processFlows/IOVariableLink"
import ProcessFlowSortationCriteria from "~/models/processFlows/processFlowSortationCriteria"
import ProcessFlowStep from '~/models/processFlows/processFlowStep'
import ProcessFlowStepElementConditionalFields from '~/models/processFlows/processFlowStepElementConditionalFields'
import ProcessFlowStepElementConditionalRuleSet from "~/models/processFlows/processFlowStepElementConditionalRuleSet"
import ProcessFlowStepJumpTo from "~/models/processFlows/processFlowStepJumpTo"
import ProcessFlowVariableDefinition from '~/models/processFlows/processFlowVariableDefinition'
import { CertificateInputMap } from "~/models/processFlows/steps/certificateInputMap"
import IterationStep from '~/models/processFlows/steps/iterationStep'
import { IterationStepVariableMap } from '~/models/processFlows/steps/iterationStepVariableMap'
import ProcessFlowMappedVariable from "~/models/processFlows/steps/processFlowMappedVariable"
import { SetDataVariableMap } from "~/models/processFlows/steps/setDataVariableMap"
import { RelationType } from "~/models/relationType"
import { Logger } from "~/services/logger"
import { ProcessFlowMappedVariableType } from "../../../formability/processFlowBuilder/enums/processFlowMappedVariableType"
import { ProcessFlowStepType } from "../../enums/processFlowStepType"
import assertNever from "../../../global/assetNever"
import { Constructor } from "../../../global/constructor"
import { ConditionalRuleEmbeddedModelsHelper } from "../conditionalRules/helpers/conditionalRuleEmbeddedModelsHelper"
import { ReferencePath } from '~/models/referencePaths/referencePath'
import ProcessFlowStepAction from '~/models/processFlows/processFlowStepAction'
import ProcessFlowCallToAction from "~/models/processFlows/callToActions/processFlowCallToAction"
import ProcessFlowCallToActionFactory from "~/models/processFlows/processFlowCallToActionFactory"

export default class ProcessFlowStepDefinition {
  static getDisplayName(type: ProcessFlowStepType): string {
    switch (type) {
      case ProcessFlowStepType.RunIteration:
        return 'Run iteration';
      case ProcessFlowStepType.CallApi:
        return 'Call an API';
      case ProcessFlowStepType.GenerateDocument:
      case ProcessFlowStepType.GenerateDocumentV2:
        return 'Generate document'
      case ProcessFlowStepType.SendNotification:
        return 'Send notification';
      case ProcessFlowStepType.RunProcess:
        return 'Run another process';
      case ProcessFlowStepType.SetData:
        return 'Set data';
      case ProcessFlowStepType.SaveData:
        return 'Save data';
      case ProcessFlowStepType.DeleteData:
        return 'Delete data';
      case ProcessFlowStepType.FindData:
        return 'Find data';
      case ProcessFlowStepType.ExportCSV:
        return "Export CSV";
      case ProcessFlowStepType.TakeAction:
        return 'Take action';
      case ProcessFlowStepType.FindCount:
        return 'Find count';
      case ProcessFlowStepType.InitialStep:
        return 'Start'
      case ProcessFlowStepType.DisplayAForm:
        return 'Display a form'
      case ProcessFlowStepType.Redirect:
        return 'Redirect'
      default:
        assertNever(type);
    }
  }

  static getIcon(type: ProcessFlowStepType): string {
    switch (type) {
      case ProcessFlowStepType.RunIteration:
        return 'refresh';
      case ProcessFlowStepType.CallApi:
        return 'api';
      case ProcessFlowStepType.GenerateDocument:
      case ProcessFlowStepType.GenerateDocumentV2:
        return 'certificate'
      case ProcessFlowStepType.SendNotification:
        return 'bell';
      case ProcessFlowStepType.RunProcess:
        return 'process-flow';
      case ProcessFlowStepType.SetData:
        return 'set-data';
      case ProcessFlowStepType.SaveData:
        return 'save-data';
      case ProcessFlowStepType.DeleteData:
        return 'wastebasket';
      case ProcessFlowStepType.InitialStep:
        return 'download'
      case ProcessFlowStepType.FindData:
        return 'find-data';
      case ProcessFlowStepType.ExportCSV:
          return "export-file";
      case ProcessFlowStepType.FindCount:
        return "find-count";
      case ProcessFlowStepType.TakeAction:
        return "actions"
      case ProcessFlowStepType.DisplayAForm:
        return 'form-approve'
      case ProcessFlowStepType.Redirect:
        return 'monitor'
      default:
        Logger.errorOnce('ProcessFlowStepDefinition.getIcon', `No step icon defined for type: ${type}`);
        return 'formability';
    }
  }

  static getColor(type: ProcessFlowStepType): string {
    switch (type) {
      case ProcessFlowStepType.RunIteration:
        return '#C84D11';
      case ProcessFlowStepType.CallApi:
        return '#257d93';
      case ProcessFlowStepType.GenerateDocument:
      case ProcessFlowStepType.GenerateDocumentV2:
        return '#9E6710'
      case ProcessFlowStepType.SendNotification:
        return '#3876CC';
      case ProcessFlowStepType.RunProcess:
        return '#AF08AA';
      case ProcessFlowStepType.SetData:
        return '#7d32b0';
      case ProcessFlowStepType.DeleteData:
        return '#cf4320';
      case ProcessFlowStepType.SaveData:
        return '#328538';
      case ProcessFlowStepType.FindData:
        return '#4B5659';
        case ProcessFlowStepType.ExportCSV:
          return '#757575';
      case ProcessFlowStepType.FindCount:
        return '#257d93';
      case ProcessFlowStepType.TakeAction:
        return '#3876CC';
      case ProcessFlowStepType.DisplayAForm:
        return '#328538';
      case ProcessFlowStepType.Redirect:
        return '#328538';
      default:
        Logger.errorOnce('ProcessFlowStepDefinition.getColor', `No step icon color defined for type: ${type}`);
        return '#E22F24'
    }
  }

  static embeddedModelDefinitionFactory(type: ProcessFlowStepType ): {[key: string]: {type: Constructor, relationType: RelationType}} {
    const embeddedModels: IEmbeddedModelHash = {};
    switch(type) {
      case ProcessFlowStepType.RunIteration:
        embeddedModels.steps = { type: ProcessFlowStep, relationType: RelationType.dictionary };
        embeddedModels.variable_map = {type: IterationStepVariableMap, relationType: RelationType.singleObject};
        break;
      case ProcessFlowStepType.GenerateDocument:
        embeddedModels.certificate_input_maps = {type: CertificateInputMap, relationType: RelationType.dictionary};
        embeddedModels.file_name = {type: ProcessFlowMappedVariable, relationType: RelationType.singleObject};
        break;
      case ProcessFlowStepType.SetData:
        embeddedModels.variable_maps = {type: SetDataVariableMap, relationType: RelationType.dictionary}
        break;
      case ProcessFlowStepType.SaveData:
        embeddedModels.variable_maps = {type: SetDataVariableMap, relationType: RelationType.dictionary}
        break;
      case ProcessFlowStepType.DeleteData:
        embeddedModels.variable_maps = {type: SetDataVariableMap, relationType: RelationType.dictionary}
        break;
      case ProcessFlowStepType.FindData:
        embeddedModels.search_criteria_mapped_variable_references = {type: ProcessFlowMappedVariable, relationType: RelationType.dictionary};
        ConditionalRuleEmbeddedModelsHelper.addEmbeddedModels(embeddedModels, ProcessFlowStepElementConditionalFields.FIND_DATA_STEP_FIELDS, FindDataStepConditionalRuleSet);
        embeddedModels.sortation_criteria = {type: ProcessFlowSortationCriteria, relationType: RelationType.dictionary};
        embeddedModels.weight_property_reference = {type: ProcessFlowMappedVariable, relationType: RelationType.singleObject};
        embeddedModels.limit_property_reference = {type: ProcessFlowMappedVariable, relationType: RelationType.singleObject};
        break;
      case ProcessFlowStepType.CallApi:
        // No embedded models for step type yet
        break;
      case ProcessFlowStepType.SendNotification:
        embeddedModels.to_recipient_reference_paths = { type: ReferencePath, relationType: RelationType.dictionary }
        embeddedModels.cc_recipient_reference_paths = { type: ReferencePath, relationType: RelationType.dictionary }
        embeddedModels.attached_document_reference_paths = { type: ReferencePath, relationType: RelationType.dictionary }
        embeddedModels.call_to_action = {type: ProcessFlowCallToAction, relationType: RelationType.singleObject};
        break;
      case ProcessFlowStepType.FindCount:
        embeddedModels.search_criteria_mapped_variable_references = {type: ProcessFlowMappedVariable, relationType: RelationType.dictionary};
        ConditionalRuleEmbeddedModelsHelper.addEmbeddedModels(embeddedModels, ProcessFlowStepElementConditionalFields.FIND_COUNT_STEP_FIELDS, FindCountStepConditionalRuleSet);
        break;
      case ProcessFlowStepType.RunProcess:
        embeddedModels.io_variable_links = {type: IOVariableLink, relationType: RelationType.dictionary}
        break;
      case ProcessFlowStepType.TakeAction:
        embeddedModels.variable_reference = {type: ProcessFlowMappedVariable, relationType: RelationType.singleObject}
        break;
      case ProcessFlowStepType.Redirect:
        embeddedModels.call_to_action = {type: ProcessFlowCallToAction, relationType: RelationType.singleObject};
        break;
    }

    embeddedModels.jump_to_mapped_variable_references = {type: ProcessFlowMappedVariable, relationType: RelationType.dictionary};
    ConditionalRuleEmbeddedModelsHelper.addEmbeddedModels(embeddedModels, ProcessFlowStepElementConditionalFields.PROCESS_FLOW_STEP_FIELDS, ProcessFlowStepElementConditionalRuleSet, ProcessFlowStepJumpTo);

    embeddedModels.actions = { type: ProcessFlowStepAction, relationType: RelationType.dictionary }
    return embeddedModels;
  }

  /**
   * Warning there are some side effects of this method
   *   Altering the "except" of the step
   *   Adding the conditional fields to the step
   * @param step
   */
  static createEmbeddedDefinitionFactory(step: ProcessFlowStep): Hash<(parent, json) => any> {
    const createEmbedded: Hash<(parent, json) => any> = {}
    step.createEmbeddedObject = createEmbedded
    switch (step.step_type) {
      case ProcessFlowStepType.RunIteration:
        createEmbedded.steps = (parent: IterationStep, json): ProcessFlowStep => {
          // I moved the step factory from here to process flow iteration step to prevent circular imports
          return parent.createStep(parent, json)
        }
        createEmbedded.variable_map = (parent: IterationStep, json): IterationStepVariableMap => {
          return IterationStepVariableMap.repo.findOrCreateWith(json.client_id, parent)
        }
        break
      case ProcessFlowStepType.CallApi:
        break
      case ProcessFlowStepType.GenerateDocument:
        createEmbedded.certificate_input_maps = (parent, json) => {
          let map = CertificateInputMap.repo.find(json.client_id)
          if (!map) {
            map = new CertificateInputMap(parent, json)
          }
          return map
        }
        createEmbedded.file_name = (parent, json) => {
          return new ProcessFlowMappedVariable("file_name", {}, {source_type: ProcessFlowMappedVariableType.Formula})
        }
        break
      case ProcessFlowStepType.SendNotification:
        createEmbedded.to_recipient_reference_paths = (parent, json): ReferencePath => {
          return ReferencePath.repo.findOrCreateWith(json.client_id, parent, json, { nameInParent: 'to_recipient_reference_paths', isCollectionRoot: true}).loadFromJson(json);
        }
        createEmbedded.cc_recipient_reference_paths = (parent, json): ReferencePath => {
          return ReferencePath.repo.findOrCreateWith(json.client_id, parent, json, { nameInParent: 'cc_recipient_reference_paths', isCollectionRoot: true}).loadFromJson(json);
        }
        createEmbedded.attached_document_reference_paths = (parent, json): ReferencePath => {
          return ReferencePath.repo.findOrCreateWith(json.client_id, parent, json, {nameInParent: 'attached_document_reference_paths', isCollectionRoot: true}).loadFromJson(json);
        }
        createEmbedded.call_to_action = (parent: ISendNotificationStep, json): ProcessFlowCallToAction => {
          return ProcessFlowCallToActionFactory.findOrCreate([json.client_id, json.id], parent, json.action_type, json, { buttonLabelRequired: true })
        }
        break
      case ProcessFlowStepType.RunProcess:
        createEmbedded.io_variable_links = (parent, json) => {
          return IOVariableLink.repo.findOrCreateWith([json.client_id], parent, json);
        }

        break
      case ProcessFlowStepType.InitialStep:
        break
      case ProcessFlowStepType.SetData:
        createEmbedded.variable_maps = (parent, json) => {
          return new SetDataVariableMap(parent).loadFromJson(json)
        }
        break
      case ProcessFlowStepType.SaveData:
        createEmbedded.variable_maps = (parent, json) => {
          return new SetDataVariableMap(parent).loadFromJson(json)
        }
        break
      case ProcessFlowStepType.DeleteData:
        createEmbedded.variable_maps = (parent, json) => {
          return new SetDataVariableMap(parent).loadFromJson(json)
        }
        break
      case ProcessFlowStepType.FindData:
        createEmbedded.search_criteria_mapped_variable_references = (parent, json) => {
          return new ProcessFlowMappedVariable('search_criteria_mapped_variable_references').loadFromJson(json);
        }
        ConditionalRuleEmbeddedModelsHelper.defineEmbeddedModelsCreation(step, ProcessFlowStepElementConditionalFields.FIND_DATA_STEP_FIELDS);
        createEmbedded.sortation_criteria = (parent, json) => {
          return ProcessFlowSortationCriteria.repo.findOrCreateWith(json.client_id, parent).loadFromJson(json);
        }
        createEmbedded.weight_property_reference = (parent, json) => {
          return ProcessFlowMappedVariable.repo.findOrCreateWith(json.client_id,'weight_property_reference');
        }
        createEmbedded.limit_property_reference = (parent, json) => {
          return ProcessFlowMappedVariable.repo.findOrCreateWith(json.client_id,'limit_property_reference');
        }
        break;
      case ProcessFlowStepType.FindCount:
        createEmbedded.search_criteria_mapped_variable_references = (parent, json) => {
          return new ProcessFlowMappedVariable('search_criteria_mapped_variable_references').loadFromJson(json);
        }
        ConditionalRuleEmbeddedModelsHelper.defineEmbeddedModelsCreation(step, ProcessFlowStepElementConditionalFields.FIND_COUNT_STEP_FIELDS);
        break;
      case ProcessFlowStepType.TakeAction:
        createEmbedded.variable_reference = (parent, json) => {
          return ProcessFlowMappedVariable.repo.findOrCreateWith(json.client_id, 'variable_reference').loadFromJson(json);
        }
        break;
      case ProcessFlowStepType.Redirect:
        createEmbedded.call_to_action = (parent: IRedirectStep, json): ProcessFlowCallToAction => {
          return ProcessFlowCallToActionFactory.findOrCreate([json.client_id, json.id], parent, json.action_type, json )
        }
        break
      default:
        break
    }

    createEmbedded.jump_to_mapped_variable_references = (parent, json) => {
      return new ProcessFlowMappedVariable('jump_to_mapped_variable_references').loadFromJson(json);
    }
    ConditionalRuleEmbeddedModelsHelper.defineEmbeddedModelsCreation(step, ProcessFlowStepElementConditionalFields.PROCESS_FLOW_STEP_FIELDS);

    createEmbedded.actions = (parent, json): ProcessFlowStepAction => {
      return new ProcessFlowStepAction(parent, json)
    }
    return createEmbedded
  }

  /**
   * When a step has a variable reference of some kind that can be assigned to a variable
   * then here are the options to choose from
   */
  static destinationVariableOptions(step: TProcessFlowStepSpecific): IVOptionSetLocal<IProcessFlowVariable> {
    // TODO: Alex: Process Flow: This needs to be restricted to the variables that the selected reference
    //   type matches. Form variable if the iterationVariable is a Form
    //   A particular form element variable (doesn't exist yet) if iterating over a repeatable form layout element, etc
    //

    let options: IProcessFlowVariable[]
    switch (step.step_type) {
      case ProcessFlowStepType.GenerateDocument:
        // Document can be placed into a File variable marked as "Single"
        options = step.processFlow.variablesHandler.all.filter(variable => {
          return variable.isVariableFile() && variable.is_single
        })
        break;
      case ProcessFlowStepType.RunIteration:
        const lastNode = step.variableReference.source_variable_path?.lastNode
        if (lastNode) {
          options = step.processFlow.variablesHandler.all.filter(variable => {
            return variable.is_single &&
              ProcessFlowVariableDefinition.matchesNodeShape(variable, lastNode as IReferencePathNodeSpecific)
          })
        } else {
          options = []
        }
        break
      case ProcessFlowStepType.FindData:
        options = step.processFlow.variablesHandler.all.filter(variable => {
          return variable.isVariableForm() || variable.isVariableObjectModel();
        });
        break;
      case ProcessFlowStepType.ExportCSV:
        options = step.processFlow.variablesHandler.all.filter(variable => {
          return variable.isVariableFile() && variable.is_single
        })
        break;
      case ProcessFlowStepType.FindCount:
        options = step.processFlow.variablesHandler.all.filter(variable => {
          return variable.isVariableNumber()
        });
        break;
      default:
        options = step.processFlow.variablesHandler.all
        break;
    }

    return {
      labelKey: 'name',
      valueKey: 'client_id',
      options,
      selectedOption: options.find(o => o.client_id === step.destinationVariable?.client_id),
    }
  }
}